import React from 'react';
import { XMarkIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/solid';

export interface Transaction {
  _id: string;
  amount: number;
  datetime: string;
  note: string;
}

interface TransactionOverlayProps {
  transaction: Transaction;
  onClose: () => void;
  onDelete: () => void;
  onEdit: () => void;
}

export function TransactionOverlay({ transaction, onClose, onDelete, onEdit }: TransactionOverlayProps) {
  const date = new Date(transaction.datetime);
  const formattedDate = date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: 'numeric'
  });
  const formattedTime = date.toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true 
  });

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Transaction Details</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <XMarkIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Transaction Info */}
        <div className="p-6 border-b border-gray-100">
          <div className="text-center mb-4">
            <div className="text-3xl font-bold text-gray-900 mb-2">
              ${transaction.amount.toFixed(2)}
            </div>
            <div className="text-sm text-gray-500">
              {formattedDate} at {formattedTime}
            </div>
          </div>
          {transaction.note && (
            <div className="text-center">
              <div className="text-gray-700 text-base font-medium">
                {transaction.note}
              </div>
            </div>
          )}
        </div>

        {/* Menu Options */}
        <div className="p-2 space-y-1">
          <button
            onClick={onEdit}
            className="w-full flex items-center gap-3 px-4 py-3 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
          >
            <PencilIcon className="w-5 h-5" />
            <span className="font-medium">Edit Transaction</span>
          </button>
          <button
            onClick={onDelete}
            className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <TrashIcon className="w-5 h-5" />
            <span className="font-medium">Delete Transaction</span>
          </button>
        </div>
      </div>
    </div>
  );
} 